import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useRecoilValue } from 'recoil';

import { hourlyWeatherPageTestIDs, Theme } from '@constants';
import { useMyWeatherHourlyForecast } from '@hooks';
import { hourlyForecastState, hourlyWeatherPageFocusIndex } from '@store';

import { roundNearest5 } from '../../../helpers/DataFormatting';
import { getForecastWeatherPhrase } from '../../../helpers/getWeatherPhrase';
import { displayString, isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TWCAccessibleContent } from '../../focusable/TWCAccessibleContent/TWCAccessibleContent';
import { TWCIcon } from '../../primitives/TWCIcon/TWCIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'hourlyWeatherPage.hourlyWeatherWidget';
const testIDPath = hourlyWeatherPageTestIDs.hourlyWeatherWidget;

const HourlyWeatherWidgetLeft = memo(({ error, iconCodeExtend, temperature, temperatureFeelsLike, weatherPhrase }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  return (
    <View style={styles.hourlyWeatherWidgetLeftWrapper}>
      <View style={styles.hourlyWeatherWidgetLeftTopRow}>
        <TWCIcon code={iconCodeExtend} height={108} type={'current'} width={108} />
        <TWCText style={{ paddingLeft: Theme.px(40) }} testID={testIDPath.temperatureText} variant={'largeText'}>
          {isDisplayable(temperature, error) ? `${temperature}°` : missingValue}
        </TWCText>
      </View>
      <View>
        <TWCText style={{ marginBottom: Theme.px(16) }} testID={testIDPath.wxPhraseText} variant={'regularText36'}>
          {displayString(weatherPhrase, error)}
        </TWCText>
        <View style={styles.hourlyWeatherWidgetFlexRowWrapper}>
          <TWCText testID={testIDPath.temperatureFeelsLikeLabelText} variant={'regularText30Transparent'}>
            {t('temperatureFeelsLikeLabel')}
          </TWCText>
          <TWCText testID={testIDPath.temperatureFeelsLikeText} variant={'regularText30'}>
            {isDisplayable(temperatureFeelsLike, error) ? ` ${temperatureFeelsLike}°` : ` ${missingValue}`}
          </TWCText>
        </View>
      </View>
    </View>
  );
});

const HourlyWeatherWidgetRightItem = memo(
  ({ error, firstText, firstTextTestID, secondText, secondTextTestID, title, titleTestID }) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    return (
      <View style={styles.hourlyWeatherWidgetRightItemWrapper}>
        <TWCText testID={titleTestID} variant={'regularText30Transparent'}>
          {t(title)}
        </TWCText>
        <View style={styles.hourlyWeatherWidgetRightItemContent}>
          <TWCText testID={firstTextTestID} variant={'regularText54'}>
            {displayString(firstText, error)}
          </TWCText>
          {secondText && !error ? (
            <TWCText testID={secondTextTestID} variant={'regularText42'}>
              {t(secondText)}
            </TWCText>
          ) : null}
        </View>
      </View>
    );
  },
);

const HourlyWeatherWidgetRight = memo(
  ({
    error,
    precipChance,
    qpf, // "quantitative precipitation forecast", aka "Precip Total"
    relativeHumidity,
    temperatureDewPoint,
    uvIndex,
    visibility,
    windDirectionCardinal,
    windGust,
    windSpeed,
  }) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const hasWind = isDisplayable(windDirectionCardinal, error) && isDisplayable(windSpeed, error);

    return (
      <View style={styles.hourlyWeatherWidgetRightWrapper}>
        <View style={styles.hourlyWeatherWidgetRightTopRow}>
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={hasWind ? `${windDirectionCardinal} ${windSpeed}` : missingValue}
            firstTextTestID={testIDPath.windText}
            secondText={hasWind && 'windUnits'}
            secondTextTestID={testIDPath.windUnitsText}
            title={'windDirectionCardinalWindSpeedLabel'}
            titleTestID={testIDPath.windTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={windGust ? t('windGustText', { windGust }) : t('windGustNone')}
            firstTextTestID={testIDPath.windGustText}
            secondText={windGust && 'windUnits'}
            secondTextTestID={testIDPath.windGustText}
            title={'windGustLabel'}
            titleTestID={testIDPath.windGustTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(precipChance, error) ? `${precipChance}%` : missingValue}
            firstTextTestID={testIDPath.precipChanceText}
            title={'precipChanceLabel'}
            titleTestID={testIDPath.precipChanceTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(qpf, error) ? `${qpf}”` : missingValue}
            firstTextTestID={testIDPath.qpfText}
            title={'qpfLabel'}
            titleTestID={testIDPath.qpfTitleText}
          />
        </View>
        <View style={styles.hourlyWeatherWidgetFlexRowWrapper}>
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(relativeHumidity, error) ? `${relativeHumidity}%` : missingValue}
            firstTextTestID={testIDPath.relativeHumidityText}
            title={'relativeHumidityLabel'}
            titleTestID={testIDPath.relativeHumidityTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(temperatureDewPoint, error) ? `${temperatureDewPoint}°` : missingValue}
            firstTextTestID={testIDPath.temperatureDewPointText}
            title={'temperatureDewPointLabel'}
            titleTestID={testIDPath.temperatureDewPointTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={displayString(uvIndex, error)}
            firstTextTestID={testIDPath.uvIndexText}
            title={'uvIndexLabel'}
            titleTestID={testIDPath.uvIndexTitleText}
          />
          <HourlyWeatherWidgetRightItem
            error={error}
            firstText={displayString(visibility, error)}
            firstTextTestID={testIDPath.visibilityText}
            secondText={isDisplayable(visibility, error) && 'visibilityUnits'}
            secondTextTestID={testIDPath.visibilityUnitsText}
            title={'visibilityLabel'}
            titleTestID={testIDPath.visibilityTitleText}
          />
        </View>
      </View>
    );
  },
);

export const HourlyWeatherWidget = ({ nextFocusUp, nextFocusDown, defaultFocus }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const focusIndex = useRecoilValue(hourlyWeatherPageFocusIndex);
  const { error, isLoading } = useRecoilValue(hourlyForecastState);
  const { params } = useRoute();
  const forecasts = useMyWeatherHourlyForecast(params?.validTimeUtc);
  const forecast = forecasts[focusIndex] ?? {};
  const { iconCodeExtend, precipChance, windGust } = forecast;

  const weatherPhrase = iconCodeExtend ? getForecastWeatherPhrase({ iconCodeExtend }) : null;

  const roundedPrecipChance = precipChance ? roundNearest5(precipChance) : null;
  const accessibilityLabel =
    error || !forecast
      ? t('errorAccessibilityLabel')
      : t('accessibilityLabel', {
          ...forecast,
          windGust: windGust ? `${windGust}mph` : 'no',
          weatherPhrase,
        });

  return (
    <>
      <View style={styles.hourlyWeatherWidgetBackground} />
      <TWCAccessibleContent
        accessibilityHint={t('accessibilityHint')}
        accessibilityLabel={accessibilityLabel}
        disabledScreenreaderFocus={defaultFocus}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={nextFocusUp}
        testID={hourlyWeatherPageTestIDs.hourlyWeatherConditionsWidget}
      >
        <View importantForAccessibility={'no-hide-descendants'} style={styles.hourlyWeatherWidgetWrapper}>
          {isLoading ? (
            <View>
              <ActivityIndicator color={Theme.loading} size={'large'} />
            </View>
          ) : (
            <View style={styles.hourlyWeatherWidgetContent}>
              <HourlyWeatherWidgetLeft error={error} {...forecast} weatherPhrase={weatherPhrase} />
              <HourlyWeatherWidgetRight error={error} {...forecast} precipChance={roundedPrecipChance} />
            </View>
          )}
        </View>
      </TWCAccessibleContent>
    </>
  );
};

const styles = StyleSheet.create({
  hourlyWeatherWidgetBackground: {
    borderRadius: Theme.px(6),
    bottom: Theme.px(6),
    height: Theme.px(480),
    opacity: 0.05,
    position: 'absolute',
    width: Theme.px(1740),
  },
  hourlyWeatherWidgetContent: {
    flexDirection: 'row',
    paddingBottom: Theme.px(122),
  },
  hourlyWeatherWidgetLeftTopRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingBottom: Theme.px(47),
    paddingLeft: Theme.px(5),
  },
  hourlyWeatherWidgetLeftWrapper: {
    width: Theme.px(312),
  },
  hourlyWeatherWidgetLoadingWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  hourlyWeatherWidgetRightItemContent: {
    alignItems: 'baseline',
    flexDirection: 'row',
  },
  hourlyWeatherWidgetRightItemWrapper: {
    alignItems: 'center',
    width: Theme.px(308),
  },
  hourlyWeatherWidgetRightTopRow: {
    flexDirection: 'row',
    paddingBottom: Theme.px(50),
  },
  hourlyWeatherWidgetRightWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-end',
  },
  hourlyWeatherWidgetWrapper: {
    height: Theme.px(300),
    width: Theme.px(1740),
  },
  hourlyWeatherWidgetFlexRowWrapper: {
    flexDirection: 'row',
  },
});
